
import React, { useState } from 'react';
import { File, Link, Upload, X, FolderOpen, Search, Plus, FileText, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

type ResourceType = 'file' | 'link';

interface Resource {
  id: string;
  name: string;
  type: ResourceType;
  url: string;
  createdAt: Date;
  size?: string;
  fileType?: string;
}

const ResourceLibrary: React.FC = () => {
  const [resources, setResources] = useState<Resource[]>([
    { 
      id: '1', 
      name: 'Q4 Business Plan.pdf', 
      type: 'file',
      url: '#',
      createdAt: new Date('2023-10-15'),
      size: '2.4 MB',
      fileType: 'pdf'
    },
    { 
      id: '2', 
      name: 'Company Wiki', 
      type: 'link',
      url: 'https://wiki.example.com',
      createdAt: new Date('2023-09-22')
    },
    { 
      id: '3', 
      name: 'Brand Guidelines.docx', 
      type: 'file',
      url: '#',
      createdAt: new Date('2023-11-05'),
      size: '1.8 MB',
      fileType: 'docx'
    },
    { 
      id: '4', 
      name: 'Marketing Strategy 2024.pptx', 
      type: 'file',
      url: '#',
      createdAt: new Date('2023-12-10'),
      size: '3.5 MB',
      fileType: 'pptx'
    },
    { 
      id: '5', 
      name: 'Product Roadmap', 
      type: 'link',
      url: 'https://roadmap.example.com',
      createdAt: new Date('2024-01-05')
    }
  ]);
  
  const [activeTab, setActiveTab] = useState<'all' | 'files' | 'links'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddingLink, setIsAddingLink] = useState(false);
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [newLinkName, setNewLinkName] = useState('');
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [isResourceDetailOpen, setIsResourceDetailOpen] = useState(false);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles: Resource[] = Array.from(e.target.files).map(file => ({
        id: `file-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        name: file.name,
        type: 'file',
        url: URL.createObjectURL(file),
        createdAt: new Date(),
        size: formatFileSize(file.size),
        fileType: file.name.split('.').pop() || ''
      }));
      
      setResources([...resources, ...newFiles]);
      toast.success(`Uploaded ${newFiles.length} file${newFiles.length > 1 ? 's' : ''} successfully`);
      
      // Reset file input
      e.target.value = '';
    }
  };

  const addLink = () => {
    if (newLinkUrl && newLinkName) {
      const newLink: Resource = {
        id: `link-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        name: newLinkName,
        type: 'link',
        url: newLinkUrl.startsWith('http') ? newLinkUrl : `https://${newLinkUrl}`,
        createdAt: new Date()
      };
      
      setResources([...resources, newLink]);
      setNewLinkUrl('');
      setNewLinkName('');
      setIsAddingLink(false);
      toast.success('Link saved successfully');
    }
  };

  const removeResource = (id: string) => {
    setResources(resources.filter(resource => resource.id !== id));
    toast.success('Resource removed');
  };

  const openResourceDetail = (resource: Resource) => {
    setSelectedResource(resource);
    setIsResourceDetailOpen(true);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredResources = resources.filter(resource => {
    // Filter by tab
    if (activeTab === 'files' && resource.type !== 'file') return false;
    if (activeTab === 'links' && resource.type !== 'link') return false;
    
    // Filter by search
    if (searchQuery) {
      return resource.name.toLowerCase().includes(searchQuery.toLowerCase());
    }
    
    return true;
  });

  // Get resource icon based on file type
  const getResourceIcon = (resource: Resource) => {
    if (resource.type === 'link') return <ExternalLink className="h-5 w-5" />;
    
    switch(resource.fileType?.toLowerCase()) {
      case 'pdf':
        return <FileText className="h-5 w-5" />;
      case 'doc':
      case 'docx':
        return <FileText className="h-5 w-5" />;
      case 'xls':
      case 'xlsx':
        return <FileText className="h-5 w-5" />;
      case 'ppt':
      case 'pptx':
        return <FileText className="h-5 w-5" />;
      default:
        return <File className="h-5 w-5" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Resource Library</h2>
          <p className="text-muted-foreground">
            Store and access important files and links
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => document.getElementById('file-upload')?.click()}>
            <Upload className="mr-2 h-4 w-4" />
            Upload File
          </Button>
          <input
            id="file-upload"
            type="file"
            multiple
            className="hidden"
            onChange={handleFileUpload}
          />
          <Button variant="outline" onClick={() => setIsAddingLink(true)}>
            <Link className="mr-2 h-4 w-4" />
            Add Link
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-6">
        <Tabs defaultValue="all" value={activeTab} onValueChange={(value) => setActiveTab(value as 'all' | 'files' | 'links')} className="w-full sm:w-auto">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="files">Files</TabsTrigger>
            <TabsTrigger value="links">Links</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search resources..."
            className="pl-8"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {filteredResources.length === 0 ? (
        <div className="text-center py-16 border rounded-lg bg-muted/10">
          <FolderOpen className="mx-auto h-12 w-12 text-muted-foreground" />
          <h3 className="mt-4 text-lg font-medium">No resources found</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
            {searchQuery ? 'Try a different search term' : 'Upload files or add links to get started'}
          </p>
          {!searchQuery && (
            <div className="mt-6 flex flex-wrap gap-2 justify-center">
              <Button
                variant="outline"
                size="sm"
                onClick={() => document.getElementById('file-upload')?.click()}
              >
                <Upload className="mr-2 h-3.5 w-3.5" />
                Upload a file
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsAddingLink(true)}
              >
                <Link className="mr-2 h-3.5 w-3.5" />
                Add a link
              </Button>
            </div>
          )}
        </div>
      ) : (
        <ScrollArea className="h-[calc(100vh-220px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pb-4">
            {filteredResources.map((resource) => (
              <Card key={resource.id} className="overflow-hidden transition-all hover:shadow-md cursor-pointer" onClick={() => openResourceDetail(resource)}>
                <CardHeader className="pb-2 relative">
                  <div className="absolute top-3 right-3">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8 rounded-full hover:bg-destructive/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeResource(resource.id);
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className={cn(
                      "h-10 w-10 rounded-md flex items-center justify-center",
                      resource.type === 'file' ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"
                    )}>
                      {getResourceIcon(resource)}
                    </div>
                    <div className="overflow-hidden">
                      <CardTitle className="text-base truncate">{resource.name}</CardTitle>
                      <CardDescription>{formatDate(resource.createdAt)}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs font-normal">
                      {resource.type === 'file' ? resource.fileType?.toUpperCase() : 'LINK'}
                    </Badge>
                    {resource.type === 'file' && resource.size && (
                      <span className="text-xs text-muted-foreground">{resource.size}</span>
                    )}
                  </div>
                  {resource.type === 'link' && (
                    <div className="text-sm text-muted-foreground truncate mt-1">
                      {resource.url}
                    </div>
                  )}
                </CardContent>
                <CardFooter className="pt-0">
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (resource.url) {
                        if (resource.type === 'link') {
                          window.open(resource.url, '_blank');
                        } else {
                          window.open(resource.url, '_blank');
                        }
                      }
                    }}
                  >
                    {resource.type === 'file' ? 'View File' : 'Open Link'}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </ScrollArea>
      )}

      {/* Add Link Dialog */}
      <Dialog open={isAddingLink} onOpenChange={setIsAddingLink}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save New Link</DialogTitle>
            <DialogDescription>Add a website or resource link to save for later</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">Link Name</label>
              <Input 
                placeholder="e.g., Company Wiki" 
                value={newLinkName} 
                onChange={(e) => setNewLinkName(e.target.value)} 
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">URL</label>
              <Input 
                placeholder="https://example.com" 
                value={newLinkUrl} 
                onChange={(e) => setNewLinkUrl(e.target.value)} 
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddingLink(false)}>Cancel</Button>
            <Button onClick={addLink} disabled={!newLinkUrl || !newLinkName}>
              Save Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resource Detail Dialog */}
      {selectedResource && (
        <Dialog open={isResourceDetailOpen} onOpenChange={setIsResourceDetailOpen}>
          <DialogContent>
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className={cn(
                  "h-10 w-10 rounded-md flex items-center justify-center",
                  selectedResource.type === 'file' ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"
                )}>
                  {getResourceIcon(selectedResource)}
                </div>
                <DialogTitle>{selectedResource.name}</DialogTitle>
              </div>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium mb-1">Type</h4>
                <p className="text-sm">
                  {selectedResource.type === 'file' 
                    ? `File (${selectedResource.fileType?.toUpperCase()})` 
                    : 'External Link'}
                </p>
              </div>
              
              {selectedResource.type === 'file' && selectedResource.size && (
                <div>
                  <h4 className="text-sm font-medium mb-1">Size</h4>
                  <p className="text-sm">{selectedResource.size}</p>
                </div>
              )}
              
              {selectedResource.type === 'link' && (
                <div>
                  <h4 className="text-sm font-medium mb-1">URL</h4>
                  <p className="text-sm break-all">{selectedResource.url}</p>
                </div>
              )}
              
              <div>
                <h4 className="text-sm font-medium mb-1">Added on</h4>
                <p className="text-sm">{formatDate(selectedResource.createdAt)}</p>
              </div>
              
              <Separator />
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setIsResourceDetailOpen(false)}
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  if (selectedResource.url) {
                    window.open(selectedResource.url, '_blank');
                  }
                }}
              >
                {selectedResource.type === 'file' ? 'View File' : 'Open Link'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ResourceLibrary;
